// TIME CAPSULES
// A buried post has open_at set to a future timestamp. Until that moment
// arrives, only the author can see it. When open_at passes, the post
// surfaces in feeds as if newly created and behaves like a normal post
// (time starts ticking toward expire_at).
//
// Lifespan math: expire_at = open_at + timeValue (not creation_at +
// timeValue), so the post's actual alive-time starts the moment it
// opens. The buried period is "free" — burial itself doesn't cost time.

import { DateTime } from 'luxon';

/** Maximum time a post can be buried. Two years from now is plenty. */
export const MAX_BURIAL_DAYS = 730;

/** Preset burial durations. Users can pick a custom date too. */
export const BURIAL_PRESETS: Array<{ label: string; days: number }> = [
  { label: '1 day',    days: 1 },
  { label: '1 week',   days: 7 },
  { label: '1 month',  days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
  { label: '1 year',   days: 365 },
];

export function isBuried(openAt: string | null | undefined, now: DateTime = DateTime.now()): boolean {
  if (!openAt) return false;
  return DateTime.fromISO(openAt) > now;
}

export function formatTimeUntilOpen(openAt: string, now: DateTime = DateTime.now()): string {
  const open = DateTime.fromISO(openAt);
  if (open <= now) return 'opening now';
  const diff = open.diff(now, ['days', 'hours', 'minutes']).toObject();
  const d = Math.floor(diff.days ?? 0);
  const h = Math.floor(diff.hours ?? 0);
  const m = Math.floor(diff.minutes ?? 0);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Absolute-date label for the open moment, user's local time. */
export function formatOpenAt(openAt: string): string {
  return DateTime.fromISO(openAt).toLocal().toFormat("LLL d, yyyy '·' h:mm a");
}
